// DESCRIPTION: Step 3 of the event creation wizard — pricing, capacity,
// DESCRIPTION: booking cutoff, buffer times, and waitlist. Two-card layout:
// DESCRIPTION: "Pricing & capacity" and "Timing", side by side on wide screens.
import { __, sprintf } from '@wordpress/i18n';
import {
	ToggleControl,
	__experimentalNumberControl as NumberControl,
} from '@wordpress/components';
import FieldError from './FieldError';

const CARD =
	'nb-rounded-xl nb-border nb-border-neutral-300 nb-bg-neutral-0 nb-p-6 nb-shadow-elevation-1';
const HEADING =
	'nb-text-base nb-font-semibold nb-text-neutral-900 nb-mt-0 nb-mb-5';
const LABEL =
	'nb-block nb-text-sm nb-font-semibold nb-text-neutral-700 nb-mb-1';

const BookingPricingStep = ( {
	formData,
	onFieldChange,
	fieldErrors = {},
} ) => {
	const isOneOnOne = formData.event_type === 'one-on-one';

	return (
		<div className="nemtly-wizard-step-content nb-grid nb-grid-cols-1 lg:nb-grid-cols-2 nb-gap-6 nb-items-start">
			{ /* Pricing & capacity */ }
			<div className={ CARD }>
				<h3 className={ HEADING }>
					{ __( 'Pricing & capacity', 'nemtly-booking' ) }
				</h3>

				<div
					className={ `nb-mb-5 ${
						fieldErrors.price ? 'nemtly-field-has-error' : ''
					}` }
				>
					<label className={ LABEL }>
						{ __( 'Price', 'nemtly-booking' ) }
						{ formData.price > 0
							? sprintf(
									/* translators: 1: formatted price */
									__( '($%1$s)', 'nemtly-booking' ),
									formData.price
							  )
							: '' }
					</label>
					<NumberControl
						value={ formData.price }
						onChange={ ( value ) =>
							onFieldChange( 'price', parseFloat( value ) || 0.0 )
						}
						min={ 0 }
						step={ 0.01 }
						prefix={
							<span className="nb-px-2 nb-text-neutral-500">
								$
							</span>
						}
						help={
							! fieldErrors.price
								? __(
										'Set to 0 for a free event.',
										'nemtly-booking'
								  )
								: undefined
						}
						__next40pxDefaultSize
					/>
					<FieldError error={ fieldErrors.price } />
				</div>

				<div className="nb-grid nb-grid-cols-1 md:nb-grid-cols-2 nb-gap-5">
					{ ! isOneOnOne && (
						<div
							className={
								fieldErrors.max_attendees
									? 'nemtly-field-has-error'
									: ''
							}
						>
							<label className={ LABEL }>
								{ formData.event_type === 'group'
									? __( 'Spots per slot', 'nemtly-booking' )
									: __(
											'Maximum attendees',
											'nemtly-booking'
									  ) }
							</label>
							<NumberControl
								value={ formData.max_attendees }
								onChange={ ( value ) =>
									onFieldChange(
										'max_attendees',
										parseInt( value ) || 0
									)
								}
								min={ 0 }
								help={
									! fieldErrors.max_attendees
										? __(
												'0 = unlimited',
												'nemtly-booking'
										  )
										: undefined
								}
								__next40pxDefaultSize
							/>
							<FieldError error={ fieldErrors.max_attendees } />
						</div>
					) }
					<div>
						<label className={ LABEL }>
							{ __(
								'Close bookings (hours ahead)',
								'nemtly-booking'
							) }
						</label>
						<NumberControl
							value={ formData.booking_cutoff }
							onChange={ ( value ) =>
								onFieldChange(
									'booking_cutoff',
									parseInt( value ) || 0
								)
							}
							min={ 0 }
							help={ __(
								'Stop bookings this many hours before the start.',
								'nemtly-booking'
							) }
							__next40pxDefaultSize
						/>
					</div>
				</div>
			</div>

			{ /* Timing */ }
			<div className={ CARD }>
				<h3 className={ HEADING }>
					{ __( 'Timing', 'nemtly-booking' ) }
				</h3>

				<div className="nb-grid nb-grid-cols-1 md:nb-grid-cols-2 nb-gap-5 nb-mb-5">
					<div>
						<label className={ LABEL }>
							{ __(
								'Prep time before (minutes)',
								'nemtly-booking'
							) }
						</label>
						<NumberControl
							value={ formData.buffer_before }
							onChange={ ( value ) =>
								onFieldChange(
									'buffer_before',
									parseInt( value ) || 0
								)
							}
							min={ 0 }
							step={ 5 }
							help={ __(
								'Blocked-off time before the start',
								'nemtly-booking'
							) }
							__next40pxDefaultSize
						/>
					</div>
					<div>
						<label className={ LABEL }>
							{ __(
								'Cooldown after (minutes)',
								'nemtly-booking'
							) }
						</label>
						<NumberControl
							value={ formData.buffer_after }
							onChange={ ( value ) =>
								onFieldChange(
									'buffer_after',
									parseInt( value ) || 0
								)
							}
							min={ 0 }
							step={ 5 }
							help={ __(
								'Blocked-off time after the end',
								'nemtly-booking'
							) }
							__next40pxDefaultSize
						/>
					</div>
				</div>

				<ToggleControl
					label={ __( 'Allow waitlist', 'nemtly-booking' ) }
					checked={ formData.allow_waitlist }
					onChange={ ( value ) =>
						onFieldChange( 'allow_waitlist', value )
					}
					help={ __(
						'Let people join a waitlist once capacity is reached',
						'nemtly-booking'
					) }
					__nextHasNoMarginBottom
				/>
			</div>
		</div>
	);
};

export default BookingPricingStep;
